import { InfoTip, SearchCombobox, type ComboOption } from '@zarya/ui';
import { t } from '@app/i18n';
import { STATES, stateLabel } from './identityVocab';
import type { ScopeIdentities, EstadoIdentidade, IdentityRole } from './Identities.api';
import s from './Identities.module.css';

/**
 * Filtros de T4 (FR-053): as pílulas de «Mostrar», com as contagens, e as caixas de
 * seleção com busca de papel, estado, site e ambiente.
 *
 * aim-pjv (decisão do mrolim, 10/10/2026): os filtros de escolha entre opções são uma
 * caixa em que se digita para achar a opção (`SearchCombobox`), e não botões. «Mostrar»
 * fica em pílulas: é o recorte com as contagens, e a contagem à vista é o que impede o
 * recorte de parecer sumiço.
 *
 * A busca fica na própria `DataTable` (`search`/`onSearchChange`), que já traz o
 * campo e o "Limpar filtros".
 */

export interface PillOption {
  value: string;
  label: string;
}

export interface FilterPillsProps {
  /** Rótulo antes das pílulas (`papel:`, `tipo:`). */
  label: string;
  options: PillOption[];
  /** `null` = a pílula "Todos" está ativa. */
  value: string | null;
  onChange: (value: string | null) => void;
  /** Rótulo da pílula que limpa a seleção. Padrão: `Todos`. */
  allLabel?: string;
  /** Explicação ⓘ ao lado do rótulo (FR-132), quando o grupo precisa de uma. */
  tip?: string;
}

/**
 * Pílulas de filtro do protótipo. Seleção única, como no protótipo: `Todos`
 * limpa, uma pílula seleciona só ela.
 *
 * O padrão de `allLabel` é resolvido no corpo, não na assinatura: um padrão de
 * parâmetro avaliado a cada render está certo, mas escrevê-lo como
 * `allLabel = t(…)` esconde que ele depende do locale.
 */
export function FilterPills({ label, options, value, onChange, allLabel, tip }: FilterPillsProps) {
  const pill = (ativo: boolean) => [s.pill, ativo ? s.pillOn : ''].filter(Boolean).join(' ');
  const rotuloTodos = allLabel ?? t('identities.filters.pills.all');

  return (
    <span className={s.pills} role="group" aria-label={label}>
      <span className={s.pillsLabel}>
        {label}
        {tip ? (
          <InfoTip
            text={tip}
            label={t('identities.filters.pills.tipLabel', { label })}
            size="xs"
          />
        ) : null}
      </span>
      <button
        type="button"
        className={pill(value === null)}
        aria-pressed={value === null}
        onClick={() => onChange(null)}
      >
        {rotuloTodos}
      </button>
      {options.map((opcao) => (
        <button
          key={opcao.value}
          type="button"
          className={pill(value === opcao.value)}
          aria-pressed={value === opcao.value}
          onClick={() => onChange(value === opcao.value ? null : opcao.value)}
        >
          {opcao.label}
        </button>
      ))}
    </span>
  );
}

export interface IdentitiesFiltersProps {
  roles: IdentityRole[];
  papelSelecionado: string | null;
  onPapelChange: (papel: string | null) => void;
  state: EstadoIdentidade | null;
  onEstadoChange: (state: EstadoIdentidade | null) => void;
  /** Recorte governadas × descobertas; `null` = as duas (30/08). */
  scope: ScopeIdentities | null;
  onEscopoChange: (scope: ScopeIdentities | null) => void;
  /** Contagens do **tenant inteiro** (`meta`), não da página nem do recorte. */
  governadas: number;
  descobertas: number;
  /**
   * Slugs dos sites do tenant. **Vazio esconde o combobox** — é a revelação
   * progressiva (P1): quem tem um cluster não ganha um filtro de uma opção só.
   * Quem decide é `useSiteSlugs`, que devolve vazio sem `multi`.
   */
  sites: string[];
  site: string | null;
  onSiteChange: (site: string | null) => void;
  /**
   * Ambientes do cadastro (Lote 3 · F1-c). Só os SELECIONÁVEIS — uma linha do eixo
   * C (metadado do catálogo do executor) nunca é ambiente de identidade, então
   * filtrar por ela devolveria sempre vazio e ensinaria o contrário.
   *
   * **Sem revelação progressiva, ao contrário do site:** o cadastro nasce com três
   * ambientes em todo tenant (a migração do F1-a semeia `dev`/`hmg`/`prod`), logo
   * não há o caso «uma opção só» que justifica esconder o de site. Vazio mesmo só
   * acontece com cadastro por carregar, e aí o combobox não tem o que oferecer.
   */
  environments: { slug: string; name: string }[];
  environment: string | null;
  onEnvironmentChange: (environment: string | null) => void;
}

/** O papel é vocabulário do tenant (o catálogo `listIdentityRoles`): o nome é dado, não vai ao catálogo. */
function roleOptions(roles: IdentityRole[]): ComboOption[] {
  return [
    { value: '', label: t('identities.filters.role.all') },
    ...roles.map((papel) => ({ value: papel.name, label: papel.name })),
  ];
}

/**
 * A primeira opção volta o filtro a "todos" — o `SearchCombobox` não tem botão
 * de limpar, e um combobox de filtro precisa de caminho de volta.
 *
 * É uma **função** desde a Onda C: como lista de módulo, os rótulos ficariam
 * congelados no idioma que estava ativo quando o arquivo foi importado.
 */
function stateOptions(): ComboOption[] {
  return [
    { value: '', label: t('identities.filters.state.all') },
    ...STATES.map((state) => ({ value: state, label: stateLabel(state) })),
  ];
}

export function IdentitiesFilters({
  roles,
  papelSelecionado,
  onPapelChange,
  state,
  onEstadoChange,
  scope,
  onEscopoChange,
  governadas,
  descobertas,
  sites,
  site,
  onSiteChange,
  environments,
  environment,
  onEnvironmentChange,
}: IdentitiesFiltersProps) {
  return (
    <>
      {/* **Primeiro filtro da barra, e o único com contagem** (30/08). É ele que
          responde "o que eu governo?" antes de "de que papel?" — a pergunta que a
          tela precisa responder ao abrir. A contagem em cada pílula é o que
          impede o recorte de parecer sumiço: o operador vê quantas ficaram do
          outro lado antes de clicar. */}
      <FilterPills
        label={t('identities.filters.scope.label')}
        options={[
          {
            value: 'governadas',
            label: t('identities.filters.scope.governedCount', { n: governadas }),
          },
          {
            value: 'descobertas',
            label: t('identities.filters.scope.discoveredCount', { n: descobertas }),
          },
        ]}
        value={scope}
        onChange={(valor) => onEscopoChange((valor as ScopeIdentities | null) ?? null)}
        allLabel={t('identities.filters.scope.allCount', { n: governadas + descobertas })}
        tip={t('identities.filters.scope.tip')}
      />
      <SearchCombobox
        label={t('identities.filters.role.label')}
        placeholder={t('identities.filters.role.placeholder')}
        searchPlaceholder={t('identities.filters.role.searchPlaceholder')}
        size="sm"
        clearable
        options={roleOptions(roles)}
        value={papelSelecionado}
        onChange={(valor) => onPapelChange((valor as string | null) || null)}
      />
      <SearchCombobox
        label={t('identities.filters.state.label')}
        placeholder={t('identities.filters.state.placeholder')}
        searchPlaceholder={t('identities.filters.state.searchPlaceholder')}
        size="sm"
        clearable
        options={stateOptions()}
        value={state}
        onChange={(valor) => onEstadoChange((valor as EstadoIdentidade | null) || null)}
      />
      {sites.length > 0 ? (
        <SearchCombobox
          label={t('identities.filters.site.label')}
          placeholder={t('identities.filters.site.placeholder')}
          searchPlaceholder={t('identities.filters.site.searchPlaceholder')}
          size="sm"
          clearable
          options={[
            // A primeira opção é o caminho de volta para «todos», como no filtro de
            // estado acima.
            { value: '', label: t('identities.filters.site.all') },
            ...sites.map((slug) => ({ value: slug, label: slug, mono: true })),
          ]}
          value={site}
          onChange={(valor) => onSiteChange((valor as string | null) || null)}
        />
      ) : null}

      {environments.length > 0 ? (
        <SearchCombobox
          label={t('identities.filters.environment.label')}
          placeholder={t('identities.filters.environment.placeholder')}
          searchPlaceholder={t('identities.filters.environment.searchPlaceholder')}
          size="sm"
          clearable
          options={[
            { value: '', label: t('identities.filters.environment.all') },
            // Rótulo `Nome (slug)` como no card de onboarding: o nome é o que a
            // pessoa reconhece, o slug é o que ela vê gravado na linha e no chip da
            // coluna «Ambiente». Mostrar só um obriga a saber o mapeamento de cabeça.
            ...environments.map((e) => ({
              value: e.slug,
              label: e.name && e.name !== e.slug ? `${e.name} (${e.slug})` : e.slug,
            })),
          ]}
          value={environment}
          onChange={(valor) => onEnvironmentChange((valor as string | null) || null)}
        />
      ) : null}
    </>
  );
}
